import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";

import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import PortalPage from "./pages/PortalPage";
import AdminPage from "./pages/AdminPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ProductsPage from "./pages/ProductsPage";
import ProductRangePage from "./pages/ProductRangePage";
import ServicesPage from "./pages/ServicesPage";
import GenesysPage from "./pages/GenesysPage";
import ShopPage from "./pages/ShopPage";
import CheckoutPage from "./pages/CheckoutPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import IntroPage from "./pages/IntroPage";
import Header from "./components/Header";
import { scrollToHashFromLocation } from "./lib/scroll";

function SmoothScroll({ children }) {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;
    let alive = true;
    function raf(time) {
      if (!alive) return;
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => {
      alive = false;
      lenis.destroy();
      if (window.__lenis === lenis) window.__lenis = null;
    };
  }, []);
  return children;
}

function HashScroll() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) scrollToHashFromLocation();
  }, [location.pathname, location.hash]);
  return null;
}

function AppShell() {
  return (
    <>
      <HashScroll />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<><Header /><LoginPage /></>} />
        <Route path="/register" element={<><Header /><RegisterPage /></>} />
        <Route path="/portal" element={<><Header /><PortalPage /></>} />
        <Route path="/admin" element={<><Header /><AdminPage /></>} />
        <Route path="/services" element={<><Header /><ServicesPage /></>} />
        <Route path="/products" element={<><Header /><ProductsPage /></>} />
        <Route path="/products/:rangeSlug/:variantSlug" element={<><Header /><ProductDetailPage /></>} />
        <Route path="/products/:rangeSlug" element={<><Header /><ProductRangePage /></>} />
        <Route path="/genesys" element={<><Header /><GenesysPage /></>} />
        <Route path="/shop" element={<><Header /><ShopPage /></>} />
        <Route path="/checkout" element={<><Header /><CheckoutPage /></>} />
        <Route path="/verify-email" element={<><Header /><VerifyEmailPage /></>} />
        <Route path="/intro" element={<IntroPage />} />
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <SmoothScroll>
            <div className="App min-h-screen bg-background text-foreground">
              <BrowserRouter>
                <AppShell />
              </BrowserRouter>
              <Toaster
                position="bottom-center"
                toastOptions={{
                  style: {
                    background: "hsl(var(--card))",
                    color: "hsl(var(--card-foreground))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "4px",
                    fontFamily: "'Manrope', sans-serif",
                  },
                }}
              />
            </div>
          </SmoothScroll>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
